import { getRumClient } from "./rum";

/**
 * Browser-side `fetch` wrapper that routes thrown errors and non-2xx
 * responses to CloudWatch RUM via {@link getRumClient}.
 *
 * Why this exists alongside RUM's built-in HTTP telemetry: aws-rum-web
 * captures HTTP request/response data automatically when the `http`
 * telemetry is enabled, but it does not surface non-2xx responses as
 * error events — they live in HTTP telemetry only and don't roll into
 * the error count or error groups dashboard. This wrapper lifts those
 * cases into the error stream so a 500-ing endpoint shows up the same
 * as a thrown TypeError would.
 *
 * Behavior:
 * - Captures the original `globalThis.fetch` at module load so consumers
 *   that later monkey-patch fetch don't get re-wrapped.
 * - On a non-2xx response: records an `Error` with `HTTP <status>
 *   <statusText> <url>` to RUM (when initialized). Does not throw —
 *   returns the response so callers handle the status as usual.
 * - On a thrown error (network failure, abort, CORS, etc.): records the
 *   error to RUM and re-throws so caller-level error handling still
 *   runs.
 * - When RUM is not initialized (SSR, RUM disabled, or before
 *   `<ObservabilityProvider>` mounts): silent passthrough.
 *
 * @example
 * ```tsx
 * import { wrappedFetch } from "@juiceco-io/observability-sdk/react";
 *
 * const response = await wrappedFetch("/api/orders");
 * if (!response.ok) {
 *   // RUM already received an error event for this; the caller's
 *   // own non-2xx handling still runs.
 * }
 * ```
 */
export const wrappedFetch: typeof fetch = async (input, init) => {
  try {
    const response = await globalFetch(input, init);
    if (!response.ok) {
      const rum = getRumClient();
      if (rum) {
        const url = resolveUrl(input);
        rum.recordError(
          new Error(`HTTP ${response.status} ${response.statusText} ${url}`),
        );
      }
    }
    return response;
  } catch (err) {
    const rum = getRumClient();
    if (rum) {
      rum.recordError(err instanceof Error ? err : new Error(String(err)));
    }
    throw err;
  }
};

function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

const globalFetch: typeof fetch =
  typeof globalThis !== "undefined" && typeof globalThis.fetch === "function"
    ? globalThis.fetch.bind(globalThis)
    : () => Promise.reject(new Error("fetch is not available in this runtime"));
